import {
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import { tap, exhaustMap, switchMap, map, from } from 'rxjs';
import { enhanceScopeData } from '@ardoq/renderers';
import {
  hideDetailsDrawer,
  openDetailsDrawer,
  setDetailsDrawerBusinessCriticalFieldIds,
  setDrawerData,
  showDetailsDrawer,
  updateDetailsDrawer,
} from 'appLayout/ardoqStudio/detailsDrawer/actions';
import { api, componentApi, entityGroupApi, traversalApi } from '@ardoq/api';
import { hideRightPane, showRightPane } from 'appContainer/actions';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { getTraversalWithStartSetAndWildcardTripleOfType } from '../utils';
import { excludeNullAndUndefined } from 'streams/utils/streamOperators';
import {
  clearLoadedState,
  deleteLoadedState,
  setTraversalContextComponent,
  toggleLoadedStateVisibility,
} from 'loadedState/actions';
import { detailsDrawerState$ } from './detailsDrawer$';
import { withLatestFrom } from 'rxjs/operators';
import { loadedState$ } from 'loadedState/loadedState$';
import { loadedStateOperations } from 'loadedState/loadedStateOperations';
import {
  APILoadTraversalResponse,
  ArdoqId,
  LoadedState,
} from '@ardoq/api-types';

const displayComponent =
  (shouldDetailsDrawerOpen: boolean) =>
  (traversalResponse: APILoadTraversalResponse) => {
    dispatchAction(
      setDrawerData({
        componentId: traversalResponse.startSetResult[0],
        scopeData: enhanceScopeData(traversalResponse.scopeData),
      })
    );
    if (!shouldDetailsDrawerOpen) {
      return;
    }
    dispatchAction(hideRightPane());
    dispatchAction(showDetailsDrawer());
  };

const showComponent = displayComponent(true);
const updateComponent = displayComponent(false);

export const getTraversalResponseForComponents = (componentIds: ArdoqId[]) =>
  from(componentApi.fetch(componentIds[0])).pipe(
    map(api.logErrorIfNeeded),
    excludeNullAndUndefined(),
    map(componentApiResponse => {
      return getTraversalWithStartSetAndWildcardTripleOfType(
        componentIds,
        componentApiResponse
      );
    })
  );

const handleOpenDetailsDrawer = routine(
  ofType(openDetailsDrawer),
  extractPayload(),
  exhaustMap(getTraversalResponseForComponents),
  exhaustMap(reqArgs => traversalApi.loadTraversal(reqArgs)),
  map(api.logErrorIfNeeded),
  excludeNullAndUndefined(),
  tap(showComponent)
);
export const handleUpdateDetailsDrawer = routine(
  ofType(updateDetailsDrawer),
  extractPayload(),
  exhaustMap(getTraversalResponseForComponents),
  exhaustMap(reqArgs => traversalApi.loadTraversal(reqArgs)),
  map(api.logErrorIfNeeded),
  excludeNullAndUndefined(),
  tap(updateComponent)
);

const closeDetailsDrawerIfComponentIsClosed = (
  loadedStates: LoadedState[],
  componentId: ArdoqId
) => {
  if (
    !loadedStateOperations.componentIdIsIncludedInLoadedState(
      loadedStates,
      componentId
    )
  ) {
    dispatchAction(hideDetailsDrawer());
  }
};

const closeDetailsDrawerRoutine = routine(
  ofType(
    deleteLoadedState,
    clearLoadedState,
    setTraversalContextComponent,
    toggleLoadedStateVisibility
  ),
  withLatestFrom(detailsDrawerState$, loadedState$),
  tap(async ([, { componentId }, { loadedStates }]) => {
    if (!componentId) {
      return;
    }
    closeDetailsDrawerIfComponentIsClosed(loadedStates, componentId);
  })
);

/**
 * There is an overlay issue between the DetailsDrawer and RightPane.
 * The drawer is always placed on top with a higher in z-index than the
 * right pane, causing an overlay if both are open. This routine listens
 * to showRightPane and close the details drawer when in viewpoint mode.
 */
const hideDetailsDrawerOnShowRightPane = routine(
  ofType(showRightPane),
  withLatestFrom(isViewpointMode$),
  tap(([, { isViewpointMode }]) => {
    if (isViewpointMode) {
      dispatchAction(hideDetailsDrawer());
    }
  })
);

const fetchBusinessCriticalFieldIdsRoutine = routine(
  ofType(openDetailsDrawer),
  switchMap(() => from(entityGroupApi.fetchBusinessCriticalFields())),
  map(api.logErrorIfNeeded),
  excludeNullAndUndefined(),
  tap(response => {
    dispatchAction(setDetailsDrawerBusinessCriticalFieldIds(response.fieldIds));
  })
);

export const detailsDrawerRoutines = collectRoutines(
  handleOpenDetailsDrawer,
  closeDetailsDrawerRoutine,
  fetchBusinessCriticalFieldIdsRoutine,
  hideDetailsDrawerOnShowRightPane
);
